import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import AppLogoImage from '../../assets/images/repraise-logo.png';
import React, { KeyboardEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitch from '../../components/Header/AvatarMenu/LanguageSwitch/LanguageSwitch';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import AuthService from '../../services/AuthService';
import { useNavigate, useSearchParams } from 'react-router-dom';

const ResetPassword = () => {
  const theme = useTheme();
  const { t } = useTranslation();

  const [validationError, setValidationError] = useState<boolean>(false);
  const [requestError, setRequestError] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const newPasswordRef = useRef<HTMLInputElement | null>(null);
  const confirmNewPasswordRef = useRef<HTMLInputElement | null>(null);

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const validateAndResetPassword = async () => {
    if (newPasswordRef.current && confirmNewPasswordRef.current) {
      if (
        newPasswordRef.current.value !== confirmNewPasswordRef.current.value ||
        newPasswordRef.current.value.length < 8 ||
        confirmNewPasswordRef.current.value.length < 8
      ) {
        setValidationError(true);
      } else {
        const resetToken = searchParams.get('reset_id');
        if (!resetToken) {
          navigate('/login');
        } else {
          const success = await AuthService.resetPassword(
            newPasswordRef.current.value,
            confirmNewPasswordRef.current.value,
            resetToken,
            setLoading
          );
          if (success) {
            setRequestError(false);
            navigate('/login/forgot-password/success', {
              state: {
                heading: t('success_heading'),
                subheading: t('reset_password_success_subheading'),
                terminateSession: true,
              },
            });
          } else {
            setRequestError(true);
          }
        }
      }
    }
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor:
          theme.palette.mode === 'light' ? theme.palette.divider : theme.palette.background.default,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          maxWidth: '500px',
        }}
      >
        <img
          width="50%"
          src={AppLogoImage}
          alt={t('app_logo_alt')}
        />
        <Typography
          variant="h4"
          sx={{ fontWeight: '700', mt: theme.spacing(15), color: theme.palette.text.primary }}
        >
          {t('reset_password')}
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{
            mt: theme.spacing(1),
            mb: theme.spacing(2),
            color: theme.palette.text.secondary,
          }}
        >
          {t('reset_password_hint')}
        </Typography>
        <FormControl
          fullWidth={true}
          sx={{ my: theme.spacing(3) }}
        >
          <TextField
            required
            autoFocus
            type={showPassword ? 'text' : 'password'}
            label={t('new_password_placeholder')}
            variant="outlined"
            size="small"
            inputRef={newPasswordRef}
            error={validationError}
            helperText={validationError ? t('check_password_message') : 'Mindestens 8 Zeichen'}
            onChange={(e) => {
              setValidationError(false);
              if (newPasswordRef.current) {
                newPasswordRef.current.value = e.target.value;
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    size="small"
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              my: theme.spacing(2),
              backgroundColor: theme.palette.background.paper,
              borderRadius: `${theme.shape.borderRadius}px`,
              '& .MuiOutlinedInput-root:hover': {
                '& > fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
          />
          <TextField
            required
            helperText={validationError ? t('check_password_message') : null}
            label={t('confirm_new_password_placeholder')}
            variant="outlined"
            size="small"
            inputRef={confirmNewPasswordRef}
            error={validationError}
            type={showPassword ? 'text' : 'password'}
            onChange={(e) => {
              setValidationError(false);
              if (confirmNewPasswordRef.current) {
                confirmNewPasswordRef.current.value = e.target.value;
              }
            }}
            onKeyDown={async (event: KeyboardEvent<HTMLDivElement>) => {
              if (event.key === 'Enter') {
                await validateAndResetPassword();
              }
            }}
            sx={{
              my: theme.spacing(2),
              backgroundColor: theme.palette.background.paper,
              borderRadius: `${theme.shape.borderRadius}px`,
              '& .MuiOutlinedInput-root:hover': {
                '& > fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
          />
          <Button
            disabled={validationError || loading}
            sx={{ my: theme.spacing(2) }}
            variant="contained"
            size="medium"
            onClick={async () => await validateAndResetPassword()}
          >
            {t('reset_password')}
          </Button>
          {requestError && (
            <Typography
              variant="subtitle2"
              sx={{ color: theme.palette.error.main, textAlign: 'center' }}
            >
              {t('request_error_retry_suggestion')}
            </Typography>
          )}
        </FormControl>
        <LanguageSwitch sx={{ p: 0 }} />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Link
            href="/login"
            variant="body1"
            underline="none"
            sx={{
              color: theme.palette.primary.main,
              mt: theme.spacing(10),
            }}
          >
            {t('back')}
          </Link>
          <Typography
            variant="body2"
            sx={{ mt: theme.spacing(5), color: theme.palette.text.secondary }}
          >
            &#169; {`${t('app_owner')} ${new Date().getFullYear()}`}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ResetPassword;
